/**
 * Public KISSED Wellness Assessment™ powered by KCIS
 *
 * Additive route mounted at /wellness-assessment. NO auth, NO cross-coupling
 * with the existing KCIS Lite / Pro UI. Fetches questions + outcomes dynamically
 * from the backend so all content edits stay server-side.
 *
 * Behaviour: 5 sections, 18 questions, 4 input types, conditional Q1 rewrites,
 * email blocklist UX, processing screen, 6 outcome variants with primary +
 * secondary (KCIS-upgrade) CTAs.
 *
 * Test IDs preserved per /app/kcis-integration/frontend-spec/PUBLIC_ASSESSMENT_SPEC.md.
 */

import { useEffect, useMemo, useState } from "react";
import "./WellnessAssessment.css";
import logo from "../assets/brand/kissed-logo.png";
import { formatApiError } from "../lib/formatError";
import {
  captureMarketingAttribution,
  readMarketingAttribution,
  attributionQueryString,
} from "../lib/marketingAttribution";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API_BASE = `${BACKEND_URL}/api`;

// Mirror of the backend BLOCKED_EMAIL_DOMAINS + test_/test123 rule. Server is authoritative.
const BLOCKED_DOMAINS = new Set([
  "example.com", "test.com", "fake.com", "placeholder.com", "dummy.com",
  "mailinator.com", "guerrillamail.com", "tempmail.com", "throwaway.com",
]);

function isBlockedEmail(email) {
  if (!email || !email.includes("@")) return true;
  const [local, domain] = email.trim().toLowerCase().split("@");
  if (BLOCKED_DOMAINS.has(domain)) return true;
  if (local.includes("test_") || local.includes("test123")) return true;
  return false;
}

function validEmailShape(email) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test((email || "").trim());
}

// funnel_key → KCIS internal route OR external URL (handled inline below).
// `kcis_clinical_assessment` now points at the Assessment Monetization Layer
// (`/clinical-assessment`) — the legacy "we'll reach out in 1 business day"
// funnel is retired for this pathway.
const FUNNEL_ROUTES = {
  kcis_clinical_assessment: "/clinical-assessment?source=wellness_gateway",
  university_pathway: "/funnels/university",
  coaching_funnel: "/funnels/coaching",
  comfort_care_inquiry: "/funnels/comfort-care",
  // clinic_external is handled with absolute URL on the outcome object
};

/* ───────────── Conditional Q1 rewrites (education-only path) ───────────── */
const LEARN_CAREERS_LABEL_FRAGMENT = "learn or change careers";
const SKIP_OPTION = { key: "skip_na", label: "Skip — not applicable to me" };

function isEducationOnly(answers) {
  const q1 = answers.q1 || [];
  if (q1.length !== 1) return false;
  return (q1[0] || "").toLowerCase().includes(LEARN_CAREERS_LABEL_FRAGMENT);
}

function applyEducationOnlyOverrides(questions, answers) {
  if (!isEducationOnly(answers)) return questions;
  return questions.map((q) => {
    if (q.id === "q2") {
      return { ...q, prompt: "How long have you been exploring your interest in wellness or healing?" };
    }
    if (q.id === "q3") {
      return {
        ...q,
        prompt: "Have you pursued any wellness education or training before?",
        options: [
          { key: "none", label: "No, this is brand new for me" },
          { key: "self_study", label: "I have done self-study (books, podcasts, courses)" },
          { key: "short_form", label: "I have taken short workshops or certifications" },
          { key: "in_depth", label: "I have completed in-depth or formal training" },
        ],
      };
    }
    if (q.id === "q4" || q.id === "q7") {
      const opts = q.options || [];
      const hasSkip = opts.some((o) => o.key === "skip_na");
      return {
        ...q,
        optional: true,
        options: hasSkip ? opts : [...opts, SKIP_OPTION],
      };
    }
    if (q.id === "q9") {
      const opts = q.options || [];
      const hasCalled = opts.some((o) => o.key === "called_to_learn");
      return hasCalled
        ? q
        : { ...q, options: [...opts, { key: "called_to_learn", label: "I feel called to learn or teach" }] };
    }
    if (q.id === "q18") {
      const opts = q.options || [];
      const hasSkip = opts.some((o) => o.key === "skip_na");
      return {
        ...q,
        prompt: "Have you observed or shadowed any holistic or functional wellness practitioner?",
        options: hasSkip ? opts : [...opts, SKIP_OPTION],
      };
    }
    return q;
  });
}

/* ───────────── Renderers ───────────── */
function MultiSelect({ question, value, onChange }) {
  const selected = new Set(value || []);
  return (
    <div className="wa-answer-list">
      {(question.options || []).map((opt, idx) => {
        const isOn = selected.has(opt.label);
        return (
          <button
            type="button"
            key={opt.key || idx}
            className={`wa-answer-card ${isOn ? "wa-answer-selected" : ""}`}
            data-testid={`answer-card-${question.id}-${idx}`}
            onClick={() => {
              const next = new Set(selected);
              if (isOn) next.delete(opt.label);
              else next.add(opt.label);
              onChange(Array.from(next));
            }}
          >
            <span className="wa-check" aria-hidden>
              {isOn ? <CheckIcon /> : null}
            </span>
            <span>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function SingleSelect({ question, value, onChange }) {
  return (
    <div className="wa-answer-list">
      {(question.options || []).map((opt, idx) => {
        const isOn = value === opt.label;
        return (
          <button
            type="button"
            key={opt.key || idx}
            className={`wa-answer-card ${isOn ? "wa-answer-selected" : ""}`}
            data-testid={`answer-card-${question.id}-${idx}`}
            onClick={() => onChange(opt.label)}
          >
            <span className="wa-check wa-check-round" aria-hidden>
              {isOn ? <DotIcon /> : null}
            </span>
            <span>{opt.label}</span>
          </button>
        );
      })}
    </div>
  );
}

function Slider({ question, value, touched, onChange }) {
  const labels = question.scale_labels || { 1: "Low", 5: "High" };
  const displayValue = touched ? value : "—";
  return (
    <div className="wa-slider-wrap">
      <div className="wa-slider-current" aria-live="polite">{displayValue}</div>
      <input
        type="range"
        min={1}
        max={5}
        step={1}
        value={value || 3}
        onChange={(e) => onChange(Number(e.target.value))}
        className="wa-slider"
        data-testid={`slider-${question.id}`}
        aria-label={question.prompt}
      />
      <div className="wa-slider-labels">
        <span>1 — {labels["1"] || labels[1]}</span>
        <span>5 — {labels["5"] || labels[5]}</span>
      </div>
    </div>
  );
}

function OpenText({ question, value, onChange }) {
  return (
    <textarea
      className="wa-textarea"
      placeholder={question.placeholder || "Take your time… (optional)"}
      data-testid="open-text-answer"
      value={value || ""}
      onChange={(e) => onChange(e.target.value)}
      rows={5}
    />
  );
}

/* ───────────── Icons (inline svg) ───────────── */
const CheckIcon = () => (
  <svg width="14" height="14" viewBox="0 0 20 20" fill="none">
    <path d="M5 10.5l3 3 7-8" stroke="currentColor" strokeWidth="2.4" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);
const DotIcon = () => (
  <svg width="10" height="10" viewBox="0 0 10 10"><circle cx="5" cy="5" r="5" fill="#fff" /></svg>
);
const ArrowRight = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none">
    <path d="M5 12h14M13 5l7 7-7 7" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/* ───────────── Logo helpers ───────────── */
const LogoBare = () => (
  <div className="wa-logo-bare" data-testid="brand-logo">
    <img src={logo} alt="KISSED by C.Von" />
  </div>
);
const LogoPill = () => (
  <div className="wa-logo-pill" data-testid="brand-logo">
    <img src={logo} alt="KISSED by C.Von" />
  </div>
);

/* ───────────── Main component ───────────── */
export default function WellnessAssessment() {
  const [stage, setStage] = useState("welcome"); // welcome | lead | question | processing | outcome
  // Persist presenting concern context (e.g. hair_loss) so it survives the
  // Square payment hop and attaches to the paid purchase on return.
  useEffect(() => {
    try {
      const ctx = new URLSearchParams(window.location.search).get("context");
      if (ctx) window.localStorage.setItem("kissed_presenting_context", ctx);
    } catch (e) { /* no-op */ }
  }, []);

  const [questionIndex, setQuestionIndex] = useState(0);
  const [definition, setDefinition] = useState(null); // {intake_type, questions, outcomes}
  const [loadError, setLoadError] = useState(null);

  const [firstName, setFirstName] = useState("");
  const [email, setEmail] = useState("");
  const [emailTouched, setEmailTouched] = useState(false);

  const [answers, setAnswers] = useState({});
  const [touchedSliders, setTouchedSliders] = useState({});

  const [submitting, setSubmitting] = useState(false);
  const [submitError, setSubmitError] = useState(null);
  const [outcome, setOutcome] = useState(null); // {pathway, recommended_funnel, lead_id, content}

  useEffect(() => {
    document.title = "KISSED Wellness Assessment powered by KCIS";
    // Sprint C: capture optional UTM/referrer attribution once per session.
    // Observability-only — never read by scoring/routing logic.
    captureMarketingAttribution();
    let cancelled = false;
    fetch(`${API_BASE}/wellness-assessment/questions`)
      .then((r) => { if (!r.ok) throw new Error(`HTTP ${r.status}`); return r.json(); })
      .then((data) => { if (!cancelled) setDefinition(data); })
      .catch((e) => { if (!cancelled) setLoadError(formatApiError(e, "Failed to load assessment")); });
    return () => { cancelled = true; };
  }, []);

  const baseQuestions = definition?.questions?.questions || [];
  const questions = useMemo(
    () => applyEducationOnlyOverrides(baseQuestions, answers),
    [baseQuestions, answers]
  );
  const sectionTitles = definition?.questions?.section_titles || {};
  const totalSections = definition?.questions?.total_sections || 5;
  const totalQuestions = questions.length || 18;
  const currentQ = questions[questionIndex];

  // ── Validation gates ──
  const emailShapeValid = validEmailShape(email);
  const emailBlocked = emailShapeValid && isBlockedEmail(email);
  const emailValid = emailShapeValid && !emailBlocked;
  const leadValid = firstName.trim().length >= 1 && emailValid;

  function canAdvance() {
    if (!currentQ) return false;
    const v = answers[currentQ.airtable_key];
    if (currentQ.optional) return true;
    switch (currentQ.type) {
      case "multi-select": return Array.isArray(v) && v.length > 0;
      case "single-select": return typeof v === "string" && v.length > 0;
      case "slider": return touchedSliders[currentQ.id] === true;
      case "open-text": return true;
      default: return false;
    }
  }

  // ── Handlers ──
  function setAnswer(question, value) {
    setAnswers((prev) => ({ ...prev, [question.airtable_key]: value }));
    if (question.type === "slider") {
      setTouchedSliders((prev) => ({ ...prev, [question.id]: true }));
    }
  }

  function next() {
    if (questionIndex < totalQuestions - 1) {
      setQuestionIndex((i) => i + 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      submitAssessment();
    }
  }

  function back() {
    if (questionIndex > 0) {
      setQuestionIndex((i) => i - 1);
      window.scrollTo({ top: 0, behavior: "smooth" });
    } else {
      setStage("lead");
    }
  }

  async function submitAssessment() {
    setSubmitting(true);
    setSubmitError(null);
    setStage("processing");
    const startedAt = Date.now();

    const payload = {
      first_name: firstName.trim(),
      email: email.trim(),
      ...answers,
      // Sprint C — observability only; never affects scoring.
      marketing_attribution: readMarketingAttribution() || undefined,
    };

    try {
      const resp = await fetch(`${API_BASE}/wellness-assessment`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
      if (!resp.ok) {
        const text = await resp.text().catch(() => "");
        let parsed;
        try { parsed = JSON.parse(text); } catch { parsed = null; }
        throw new Error(formatApiError(parsed || text, `HTTP ${resp.status}`));
      }
      const data = await resp.json();
      const elapsed = Date.now() - startedAt;
      const wait = Math.max(0, 2200 - elapsed); // ≥ ~2s processing
      setTimeout(() => {
        const oc = (definition?.outcomes || {})[data.pathway] || {};
        setOutcome({
          lead_id: data.id,
          pathway: data.pathway,
          recommended_funnel: data.recommended_funnel,
          content: oc,
        });
        setStage("outcome");
        setSubmitting(false);
      }, wait);
    } catch (e) {
      setSubmitError(formatApiError(e, "Submission failed"));
      setSubmitting(false);
      setStage("question"); // bounce back so they can retry
    }
  }

  async function handleCtaClick(funnelKey, externalUrl) {
    if (!outcome?.lead_id || !funnelKey) return;
    try {
      await fetch(`${API_BASE}/wellness-assessment/${outcome.lead_id}/funnel-clicked`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ funnel_key: funnelKey }),
      });
    } catch (_) { /* swallow — non-blocking analytics */ }
    if (externalUrl) {
      // External destination — append UTM passthrough only (no lead_id).
      const sep = externalUrl.includes("?") ? "&" : "?";
      const q = attributionQueryString();
      window.location.href = q ? `${externalUrl}${sep}${q.slice(1)}` : externalUrl;
      return;
    }
    const route = FUNNEL_ROUTES[funnelKey];
    if (route) {
      // Sprint C: propagate lead_id (+ UTM passthrough) so the funnel can
      // render the ConfirmCard and skip duplicate name/email collection.
      const params = new URLSearchParams();
      params.set("source_lead_id", outcome.lead_id);
      params.set("fn", funnelKey);
      const utm = attributionQueryString();
      const url = `${route}?${params.toString()}${utm}`;
      window.location.href = url;
    }
  }

  function restart() {
    setStage("welcome");
    setQuestionIndex(0);
    setAnswers({});
    setTouchedSliders({});
    setFirstName(""); setEmail(""); setEmailTouched(false);
    setOutcome(null); setSubmitError(null);
    window.scrollTo({ top: 0, behavior: "instant" });
  }

  // ── Loading / error states ──
  if (loadError) {
    return (
      <div className="wa-root">
        <div className="wa-screen wa-screen-light">
          <div className="wa-container" style={{ alignItems: "center", justifyContent: "center", flex: 1, textAlign: "center" }}>
            <p className="wa-error-text">Could not load assessment: {loadError}</p>
            <button className="wa-btn wa-btn-primary" onClick={() => window.location.reload()} style={{ marginTop: 16 }}>Retry</button>
          </div>
        </div>
      </div>
    );
  }
  if (!definition) {
    return (
      <div className="wa-root">
        <div className="wa-screen wa-screen-light">
          <div className="wa-container" style={{ alignItems: "center", justifyContent: "center", flex: 1 }}>
            <div className="wa-pulse-ring" />
          </div>
        </div>
      </div>
    );
  }

  /* ───────── Welcome ───────── */
  if (stage === "welcome") {
    return (
      <div className="wa-root">
        <div className="wa-screen wa-screen-dark">
          <div className="wa-container wa-welcome">
            <LogoBare />
            <div className="wa-eyebrow">KISSED Wellness Assessment</div>
            <h1 className="wa-heading">Your wellness story<br/>deserves to be understood.</h1>
            <p>
              An 18-question assessment to help you identify the pathway that aligns with where
              you are right now — clinical wellness, coaching, education, comfort care, or a
              deeper KCIS Clinical Assessment™.
            </p>
            <button
              className="wa-btn wa-btn-inverted"
              data-testid="begin-assessment-button"
              onClick={() => setStage("lead")}
              style={{ marginTop: 12 }}
            >
              Begin Assessment <ArrowRight />
            </button>
            <p style={{ fontSize: 12, opacity: 0.7, marginTop: 24 }}>
              Free · ~5 minutes · No login required
            </p>
          </div>
        </div>
      </div>
    );
  }

  /* ───────── Lead capture ───────── */
  if (stage === "lead") {
    return (
      <div className="wa-root">
        <div className="wa-screen wa-screen-light">
          <div className="wa-container">
            <div className="wa-header-row">
              <LogoPill />
              <div className="wa-eyebrow" style={{ color: "#4B0082" }}>Step 1 of 2</div>
            </div>
            <h2 className="wa-heading" style={{ fontSize: "clamp(26px, 4vw, 36px)", margin: "0 0 8px" }}>
              First, a little about you.
            </h2>
            <p style={{ color: "var(--wa-text-muted)", margin: "0 0 28px", fontSize: 15 }}>
              We use this only to send you your personalized pathway recommendation.
            </p>

            <div className="wa-form-card">
              <label className="wa-label" htmlFor="wa-first-name">First name</label>
              <input
                id="wa-first-name"
                type="text"
                className="wa-input"
                data-testid="first-name-input"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Your first name"
                autoComplete="given-name"
              />

              <label className="wa-label" htmlFor="wa-email">Email</label>
              <input
                id="wa-email"
                type="email"
                className={`wa-input ${emailTouched && !emailValid ? "wa-input-error" : ""}`}
                data-testid="email-input"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                onBlur={() => setEmailTouched(true)}
                placeholder="you@yourdomain.com"
                autoComplete="email"
              />
              {emailTouched && !emailValid && (
                <p className="wa-error-text" data-testid="email-error">
                  Please enter your real email address to receive your personalized results.
                </p>
              )}

              <div style={{ display: "flex", gap: 12, marginTop: 16 }}>
                <button className="wa-btn wa-btn-ghost" onClick={() => setStage("welcome")}>Back</button>
                <button
                  className="wa-btn wa-btn-primary"
                  data-testid="submit-lead-button"
                  disabled={!leadValid}
                  onClick={() => { setEmailTouched(true); if (leadValid) setStage("question"); }}
                  style={{ flex: 1 }}
                >
                  Continue <ArrowRight />
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ───────── Processing ───────── */
  if (stage === "processing") {
    return (
      <div className="wa-root">
        <div className="wa-screen wa-screen-light">
          <div className="wa-container wa-processing">
            <div className="wa-pulse-ring" />
            <div>
              <div className="wa-eyebrow" style={{ color: "#4B0082" }}>Analyzing</div>
              <p style={{ fontSize: 18, marginTop: 8, fontFamily: "'Playfair Display', Georgia, serif", color: "#4B0082" }}>
                Analyzing your wellness patterns…
              </p>
            </div>
          </div>
        </div>
      </div>
    );
  }

  /* ───────── Outcome ───────── */
  if (stage === "outcome" && outcome) {
    const c = outcome.content || {};
    const hasSecondary = !!c.secondary_cta;
    // The "KCIS upgrade" semantics only apply when the pathway is NOT already KCIS.
    // For pathway === "kcis" the secondary CTA is a downsell (book standard consult).
    const isKcisUpgradeLink = hasSecondary && outcome.pathway !== "kcis";
    return (
      <div className="wa-root">
        <div className="wa-screen wa-screen-dark">
          <div className="wa-container wa-outcome">
            <LogoBare />
            <div className="wa-eyebrow" data-testid="outcome-eyebrow">{c.eyebrow || "Your aligned next step"}</div>
            <h1 className="wa-heading wa-outcome-headline" data-testid="outcome-headline">
              {c.headline || "Here is your aligned next step."}
            </h1>
            <p className="wa-outcome-body">{c.body}</p>

            {c.next_step_copy && (
              <div
                className="wa-outcome-next-step"
                data-testid="outcome-aligned-next-step"
              >
                <span className="wa-outcome-step-label">Your aligned next step</span>
                <p>{c.next_step_copy}</p>
              </div>
            )}

            {c.booking_guidance && (
              <div
                className="wa-outcome-booking"
                data-testid="outcome-booking-guidance"
              >
                <span className="wa-outcome-step-label">What this looks like</span>
                <p>{c.booking_guidance}</p>
              </div>
            )}

            <div className="wa-outcome-actions">
              {c.primary_cta && (
                <button
                  className="wa-btn wa-btn-inverted"
                  data-testid="outcome-primary-cta"
                  onClick={() => handleCtaClick(c.primary_cta.funnel_key, c.primary_cta.url)}
                >
                  {c.primary_cta.label}
                </button>
              )}
              {hasSecondary && (
                <button
                  className="wa-link"
                  data-testid={isKcisUpgradeLink ? "kcis-upgrade-link" : "outcome-secondary-cta"}
                  onClick={() => handleCtaClick(c.secondary_cta.funnel_key, c.secondary_cta.url)}
                >
                  {c.secondary_cta.label}
                </button>
              )}
            </div>

            {c.expectation_copy && (
              <p
                className="wa-outcome-expectation"
                data-testid="outcome-expectation-copy"
              >
                {c.expectation_copy}
              </p>
            )}

            <button className="wa-link" data-testid="restart-button" onClick={restart} style={{ marginTop: 16, opacity: 0.6 }}>
              Restart assessment
            </button>
          </div>
        </div>
      </div>
    );
  }

  /* ───────── Question screen ───────── */
  if (!currentQ) return null;
  const progressPct = ((questionIndex + 1) / totalQuestions) * 100;
  const sectionNum = currentQ.section;
  const v = answers[currentQ.airtable_key];

  let renderer = null;
  if (currentQ.type === "multi-select") renderer = <MultiSelect question={currentQ} value={v || []} onChange={(nv) => setAnswer(currentQ, nv)} />;
  else if (currentQ.type === "single-select") renderer = <SingleSelect question={currentQ} value={v || ""} onChange={(nv) => setAnswer(currentQ, nv)} />;
  else if (currentQ.type === "slider") renderer = <Slider question={currentQ} value={v || 3} touched={touchedSliders[currentQ.id]} onChange={(nv) => setAnswer(currentQ, nv)} />;
  else if (currentQ.type === "open-text") renderer = <OpenText question={currentQ} value={v || ""} onChange={(nv) => setAnswer(currentQ, nv)} />;

  const isLast = questionIndex === totalQuestions - 1;

  return (
    <div className="wa-root">
      <div className="wa-screen wa-screen-light">
        <div className="wa-container">
          <div className="wa-progress-bar" data-testid="progress-bar">
            <div className="wa-progress-meta">
              <span>Section {sectionNum} of {totalSections} — {sectionTitles[sectionNum] || ""}</span>
              <span>{questionIndex + 1} / {totalQuestions}</span>
            </div>
            <div className="wa-progress-track">
              <div className="wa-progress-fill" style={{ width: `${progressPct}%` }} />
            </div>
          </div>

          <div className="wa-question-body">
            <div>
              <h2 className="wa-heading wa-question-prompt">{currentQ.prompt}</h2>
              {currentQ.helper && <p className="wa-question-helper">{currentQ.helper}</p>}
            </div>
            {renderer}
            {submitError && (
              <p className="wa-error-text" role="alert">{submitError}</p>
            )}
          </div>

          <div className="wa-nav">
            <button
              className="wa-btn wa-btn-ghost"
              data-testid="back-question-button"
              onClick={back}
            >
              Back
            </button>
            <button
              className="wa-btn wa-btn-primary"
              data-testid="next-question-button"
              disabled={!canAdvance() || submitting}
              onClick={next}
            >
              {isLast ? "See My Results" : "Next"} <ArrowRight />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
